import { useEffect, useState } from "react";
import { AlignLeft, List } from "lucide-react";
import Modal from "../../../../components/Modal/Modal";
import Button from "../../../../components/Button/Button";
import Input from "../../../../components/Input/Input";
import { SECTION_TYPES } from "../../LessonPlannerConstant";

const BLOCK_TYPES = [
  {
    type: SECTION_TYPES.RICH_TEXT,
    icon: AlignLeft,
    title: "Rich Text",
    description: "Paragraphs, explanations, and detailed descriptive content",
  },
  {
    type: SECTION_TYPES.BULLET_LIST,
    icon: List,
    title: "Bullet List",
    description: "Learning objectives, key points, or any enumerated items",
  },
];

const INITIAL = { name: "", sub_heading: "", type: SECTION_TYPES.RICH_TEXT };

/** "Choose a Block Type" — adds a custom section to the lesson plan. */
const AddSectionModal = ({ isOpen, onClose, onAdd }) => {
  const [section, setSection] = useState(INITIAL);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setSection(INITIAL);
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSection((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // This modal is a portal inside the lesson plan <form>; React bubbles portal
    // events through the component tree, so stop it from submitting the lesson plan
    event.stopPropagation();
    const nextErrors = {};
    if (!section.name.trim()) nextErrors.name = "Section name is required";
    if (!section.sub_heading.trim()) nextErrors.sub_heading = "Sub-heading is required";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onAdd({ ...section, name: section.name.trim(), sub_heading: section.sub_heading.trim() });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={
        <span className="lp-modal-title lp-modal-title--left">
          Choose a Block Type
          <small>Select the type of content you want to add to your lesson plan.</small>
        </span>
      }
      footer={
        <div className="lp-modal-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="lp-add-section-form">
            Continue
          </Button>
        </div>
      }
    >
      <form id="lp-add-section-form" onSubmit={handleSubmit} noValidate>
        <Input
          label="Section Name"
          name="name"
          id="lp-section-name"
          value={section.name}
          onChangeHandler={handleChange}
          placeholder="e.g. Warm-up Activity"
          required
          error={errors.name}
        />
        <Input
          label="Sub - Heading"
          name="sub_heading"
          id="lp-section-sub-heading"
          value={section.sub_heading}
          onChangeHandler={handleChange}
          placeholder="e.g. Students will be learning this"
          required
          error={errors.sub_heading}
        />
        <div className="lp-block-types" role="radiogroup" aria-label="Block type">
          {BLOCK_TYPES.map(({ type, icon: Icon, title, description }) => (
            <Button
              key={type}
              variant="plain"
              role="radio"
              aria-checked={section.type === type}
              className={`lp-btn-plain lp-block-type ${section.type === type ? "is-selected" : ""}`}
              onClick={() => setSection((prev) => ({ ...prev, type }))}
            >
              <span className="lp-block-type__icon">
                <Icon size={16} />
              </span>
              <strong>{title}</strong>
              <span>{description}</span>
            </Button>
          ))}
        </div>
      </form>
    </Modal>
  );
};

export default AddSectionModal;
